/**
 * ============================================================================
 *  File        : frontend/src/pages/MasterPages.tsx
 *  Module      : pages
 *  Purpose     : 主檔（spec §B）5 個獨立全 CRUD 頁 — 品目 / 素材 / 場地 / 台秤 / 倉庫。
 *                每頁各自路由 + nav 入口，list（GET）+ 新增（POST）+ 修改（PATCH /:id）
 *                + 停用/啟用（toggle is_active via PATCH，無 hard delete；spec §B
 *                「已使用品目不得 hard delete」）。欄位定義直接重用 navigation.ts 的
 *                masterResources（單一真相，不再重新推導）。
 *  Author      : EF Engineering
 *  Created     : 2026-06-02
 *  Updated     : 2026-06-02
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  後端：GET/POST /api/v1/master/<x>、PATCH /api/v1/master/<x>/:id（master_routes，
 *  RBAC = system.configs.manage）。後端不提供 hard delete；停用/啟用 = PATCH is_active
 *  （ResourceWorkbench 的 is_active checkbox 即此用途）。
 */

import type { ReactElement } from 'react'
import { ResourceWorkbench } from '../components/ResourceWorkbench'
import { masterResources } from '../data/navigation'
import type { ResourceConfig } from '../types'

interface PageProps {
  t: (key: string) => string
}

/** 依 id 取 masterResources 設定（缺漏即為開發期錯誤，立即拋出）。 */
function resourceById(id: string): ResourceConfig {
  const config = masterResources.find((resource) => resource.id === id)
  if (!config) {
    throw new Error(`master resource config not found: ${id}`)
  }
  return config
}

/** 單一主檔頁外殼：標題列 + 可重用 CRUD workbench。 */
function MasterResourcePage({
  config,
  t,
}: {
  config: ResourceConfig
  t: (key: string) => string
}): ReactElement {
  return (
    <section className="page-stack" data-testid={`master-page-${config.id}`}>
      <header className="page-heading">
        <div>
          <span className="eyebrow">{t('nav.masterData')}</span>
          <h1>{t(config.titleKey)}</h1>
          <p>{t('desc.masterData')}</p>
        </div>
      </header>
      <ResourceWorkbench config={config} t={t} />
    </section>
  )
}

export function MasterCategoriesPage({ t }: PageProps): ReactElement {
  return <MasterResourcePage config={resourceById('categories')} t={t} />
}

export function MasterMaterialsPage({ t }: PageProps): ReactElement {
  return <MasterResourcePage config={resourceById('materials')} t={t} />
}

export function MasterVenuesPage({ t }: PageProps): ReactElement {
  return <MasterResourcePage config={resourceById('venues')} t={t} />
}

export function MasterScalesPage({ t }: PageProps): ReactElement {
  return <MasterResourcePage config={resourceById('scales')} t={t} />
}

export function MasterWarehousesPage({ t }: PageProps): ReactElement {
  return <MasterResourcePage config={resourceById('warehouses')} t={t} />
}
